import type { ICollectableCoupon } from '@/interfaces/collectableCoupon';
import { buildGrid, type GridResult } from './grid';

/**
 * Compute the page the swiper should land on after a coupon has been
 * collected. Mirrors the count-driven grid rule used in render so the
 * advance math agrees with the actual layout on the next render.
 *
 * Returns:
 *   - `newPage` = target page index (clamped to the safePage input or
 *     one past it if the current page is now empty).
 *   - `grid` = the same GridResult the render will use.
 *
 * The caller is responsible for invoking
 * `swiperRef.current?.slideToLoop(newPage)` and for not advancing when
 * `newPage === safePage`.
 */
export const computeAdvancePage = (
  remaining: ICollectableCoupon[],
  safePage: number,
  pageSize: number,
  viewportHeight: number,
): { newPage: number; grid: GridResult } => {
  const grid = buildGrid(remaining.length, pageSize, viewportHeight);
  const totalPages = grid.totalPages;
  const slidesPerPage = grid.slidesPerPage;

  const currentTargetPage = Math.min(safePage, totalPages - 1);
  const currentPageStart = currentTargetPage * slidesPerPage;
  const currentPageCoupons = remaining.slice(currentPageStart, currentPageStart + slidesPerPage);

  const newPage =
    currentPageCoupons.length > 0
      ? currentTargetPage
      : Math.min(currentTargetPage + 1, totalPages - 1);

  return { newPage, grid };
};